/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Graph Node DOM Element
   Renders node header, bypass tag, ports, thumbnail preview,
   and triggers wire creation.
   ═══════════════════════════════════════════════════════════════ */

import React, { memo, useRef, useEffect } from 'react';
import type { NodeId, NodeInstance, PortId, PortType } from '../../domain/types';
import { CategoryGlyph, PortTypeGlyph } from '../../components/common/Icons';
import { getTextureFromMedia } from '../../rendering/media';
import { useDomainBridge } from '../../stores/domainBridge';
import styles from './NodeElement.module.css';

interface NodeElementProps {
  node: NodeInstance;
  category: string;
  selected: boolean;
  onSelect: (nodeId: NodeId, additive: boolean) => void;
  onDragStart: (nodeId: NodeId, startX: number, startY: number, pointerId: number) => void;
  onPortPinMeasure: (nodeId: NodeId, portId: PortId, isOutput: boolean, x: number, y: number) => void;
  onStartWire: (nodeId: NodeId, portId: PortId, type: PortType, x: number, y: number, pointerId: number) => void;
  onEndWire: (nodeId: NodeId, portId: PortId) => void;
  onToggleCollapse: (nodeId: NodeId) => void;
}

const PORT_COLOR_MAP: Record<PortType, string> = {
  color: '#e8b4f8',
  number: '#a9e1f7',
  image: '#8fe46c',
  mask: '#f8d84e',
  text: '#f0f0ee',
  boolean: '#63d9db',
  any: '#9b9b97',
};

function getOffsetWithin(element: HTMLElement, ancestor: HTMLElement): { x: number; y: number } {
  let x = element.offsetWidth / 2;
  let y = element.offsetHeight / 2;
  let current: HTMLElement | null = element;
  while (current && current !== ancestor) {
    x += current.offsetLeft;
    y += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }
  return { x, y };
}

const NodeElementComponent: React.FC<NodeElementProps> = ({
  node,
  category,
  selected,
  onSelect,
  onDragStart,
  onPortPinMeasure,
  onStartWire,
  onEndWire,
  onToggleCollapse,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputPinRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const outputPinRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const thumbCanvasRef = useRef<HTMLCanvasElement>(null);

  const scheduler = useDomainBridge((state) => state.scheduler);
  const outputValue = useDomainBridge((state) => state.nodeOutputs[node.id]);

  // Measure port pin coordinates relative to the graph canvas
  useEffect(() => {
    const reportPositions = () => {
      const container = containerRef.current;
      if (!container) return;

      for (const [portId, el] of Object.entries(inputPinRefs.current)) {
        if (el) {
          const offset = getOffsetWithin(el, container);
          onPortPinMeasure(node.id, portId, false, offset.x, offset.y);
        }
      }

      for (const [portId, el] of Object.entries(outputPinRefs.current)) {
        if (el) {
          const offset = getOffsetWithin(el, container);
          onPortPinMeasure(node.id, portId, true, offset.x, offset.y);
        }
      }
    };

    reportPositions();
  }, [node.collapsed, onPortPinMeasure, node.id]);

  // Extract visual outputs for thumbnail
  let previewTexture: import('../../rendering/types').TextureHandle | null = null;
  let previewColor: string | null = null;
  let previewNumber: number | null = null;

  if (outputValue) {
    for (const val of Object.values(outputValue)) {
      const tex = getTextureFromMedia(val);
      if (tex && tex.nativeHandle) {
        previewTexture = tex;
        break;
      } else if (typeof val === 'string' && val.startsWith('#')) {
        previewColor = val;
      } else if (typeof val === 'number') {
        previewNumber = val;
      }
    }
  }

  // Render thumbnail onto mini canvas
  useEffect(() => {
    if (previewTexture && thumbCanvasRef.current) {
      scheduler.renderToCanvas(previewTexture, thumbCanvasRef.current, 180);
    }
  }, [previewTexture, scheduler]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest(`.${styles.portPin}`)) {
      return;
    }
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    // Preserve an existing multi-selection when beginning a drag on one of its nodes.
    if (additive || !selected) onSelect(node.id, additive);
    onDragStart(node.id, e.clientX, e.clientY, e.pointerId);
  };

  const handleOutputPinPointerDown = (portId: PortId, type: PortType, e: React.PointerEvent) => {
    e.stopPropagation();
    const pin = outputPinRefs.current[portId];
    if (!pin) return;
    const rect = pin.getBoundingClientRect();
    onStartWire(node.id, portId, type, rect.left + rect.width / 2, rect.top + rect.height / 2, e.pointerId);
  };

  const handleInputPinPointerUp = (portId: PortId, e: React.PointerEvent) => {
    e.stopPropagation();
    onEndWire(node.id, portId);
  };

  return (
    <div
      ref={containerRef}
      data-node-id={node.id}
      className={`${styles.nodeContainer} ${selected ? styles.selected : ''} ${node.bypassed ? styles.bypassed : ''}`}
      style={{
        transform: `translate3d(${node.position.x}px, ${node.position.y}px, 0)`,
      }}
      onPointerDown={handlePointerDown}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.categoryTag}>
            <CategoryGlyph category={category} size={14} />
          </span>
          <span className={styles.title} title={node.meta?.label || node.type}>
            {node.meta?.label || node.type}
          </span>
          {node.bypassed && <span className={styles.bypassTag}>BYPASS</span>}
        </div>
        <div className={styles.headerRight}>
          <button
            className={styles.iconBtn}
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse(node.id);
            }}
            title={node.collapsed ? 'Expand' : 'Collapse'}
          >
            {node.collapsed ? '+' : '−'}
          </button>
        </div>
      </div>

      {/* Body: Ports */}
      {!node.collapsed && (
        <div className={styles.portsArea}>
          {/* Inputs Column */}
          <div className={styles.inputColumn}>
            {Object.values(node.inputs).map((port) => {
              const pinColor = PORT_COLOR_MAP[port.type] || '#9b9b97';
              return (
                <div key={port.id} className={styles.portRow}>
                  <div
                    ref={(el) => {
                      inputPinRefs.current[port.id] = el;
                    }}
                    className={`${styles.portPin} ${styles.inputPin}`}
                    style={{ borderColor: pinColor }}
                    onPointerUp={(e) => handleInputPinPointerUp(port.id, e)}
                    title={`Input: ${port.name} (${port.type})`}
                  />
                  <PortTypeGlyph type={port.type} size={10} />
                  <span className={styles.portLabel}>{port.name}</span>
                </div>
              );
            })}
          </div>

          {/* Outputs Column */}
          <div className={styles.outputColumn}>
            {Object.values(node.outputs).map((port) => {
              const pinColor = PORT_COLOR_MAP[port.type] || '#9b9b97';
              return (
                <div key={port.id} className={styles.portRow}>
                  <span className={styles.portLabel}>{port.name}</span>
                  <PortTypeGlyph type={port.type} size={10} />
                  <div
                    ref={(el) => {
                      outputPinRefs.current[port.id] = el;
                    }}
                    className={`${styles.portPin} ${styles.outputPin}`}
                    style={{ background: pinColor, borderColor: pinColor }}
                    onPointerDown={(e) => handleOutputPinPointerDown(port.id, port.type, e)}
                    title={`Output: ${port.name} (${port.type})`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Thumbnail preview for visual nodes */}
      {!node.collapsed && previewTexture && (
        <div className={styles.nodeThumbnail}>
          <canvas ref={thumbCanvasRef} className={styles.nodeThumbnailCanvas} />
        </div>
      )}

      {/* Inline result hint */}
      {!node.collapsed && previewColor && (
        <div className={styles.inlinePreview}>
          <div className={styles.colorSwatch} style={{ backgroundColor: previewColor }} />
          <span>{previewColor}</span>
        </div>
      )}
      {!node.collapsed && previewNumber !== null && previewNumber !== undefined && (
        <div className={styles.inlinePreview}>
          <span>Output: {previewNumber.toFixed(2)}</span>
        </div>
      )}
    </div>
  );
};

export const NodeElement = memo(NodeElementComponent);
